import { useState, useEffect, useRef } from "react"
import { ChevronDown, CalendarDays } from "lucide-react"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { ClimbingBoxLoader } from "react-spinners"
import axios from "axios"
import API_BASE_URL from "../../Config" // use local config module instead of missing ../../Config
import { Toast } from "primereact/toast"

const Returns = () => {
  const toastRef = useRef(null)

  const [formData, setFormData] = useState({
    projectName: "",
    licenseNumber: "",
    monthRange: "",
    year: null,
  })

  const [filterData, setFilterData] = useState({
    projectNames: [],
    licenseNumbers: [],
    projectLicenseMap: {}, // Added mapping between projects and licenses
  })

  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchTerms, setSearchTerms] = useState({
    projectName: "",
    licenseNumber: "",
  })
  const [isLoading, setIsLoading] = useState(false)

  const dropdownScrollRef = useRef(null)
  const searchInputRef = useRef(null)

  const monthRangeOptions = ["Jan-Jun", "Jul-Dec"]

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const client_id = sessionStorage.getItem("client_id")
        const role_id = sessionStorage.getItem("user_role_id")
        const username = sessionStorage.getItem("current_username") || sessionStorage.getItem("current_username")
        const authToken =
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("access_token")

        if (!client_id || !role_id || !username) {
          toastRef.current?.show({
            severity: "error",
            summary: "Authentication Error",
            detail: "Please login to access this feature. Session data is missing.",
            life: 4000,
          })
          return
        }

        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        }

        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`
        }

        const response = await axios.post(
          `${API_BASE_URL}api/returns_filter_data/`,
          {
            client_id,
            role_id,
            username,
          },
          { headers },
        )

        const apiData = response.data.data || []

        const projectNames = []
        const licenseNumbers = []
        const projectLicenseMap = {}
        const seenProjects = new Set()
        const seenLicenses = new Set()

        apiData.forEach((item) => {
          if (item.project_name && !seenProjects.has(item.project_name)) {
            projectNames.push(item.project_name)
            seenProjects.add(item.project_name)

            // Map project to its license number
            if (item.license_number) {
              projectLicenseMap[item.project_name] = item.license_number
            }
          }

          if (item.license_number && !seenLicenses.has(item.license_number)) {
            licenseNumbers.push(item.license_number)
            seenLicenses.add(item.license_number)
          }
        })

        setFilterData({
          projectNames,
          licenseNumbers,
          projectLicenseMap,
        })
      } catch (error) {
        if (error.response?.status === 401) {
          toastRef.current?.show({
            severity: "error",
            summary: "Authentication Required",
            detail: "Please login again to access this feature.",
            life: 4000,
          })
        } else if (error.response?.status === 500) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: "SOMETHING WENT WRONG!!!",
            life: 4000,
          })
        } else if (error.response?.data?.detail) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: error.response.data.detail,
            life: 4000,
          })
        } else if (error.response?.data?.message) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: error.response.data.message,
            life: 4000,
          })
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load filter data. Please refresh the page.",
            life: 4000,
          })
        }
      }
    }

    fetchFilterData()
  }, [])

  const toggleDropdown = (field) => {
    setOpenDropdown(openDropdown === field ? null : field)
  }

  const handleOptionSelect = (field, option) => {
    if (field === "projectName") {
      const matchingLicense = filterData.projectLicenseMap[option]

      if (matchingLicense) {
        setFormData((prev) => ({
          ...prev,
          projectName: option,
          licenseNumber: matchingLicense,
        }))
      } else {
        toastRef.current.show({
          severity: "error",
          summary: "No Match Found",
          detail: "There is no matching licence number for this project name!!",
          life: 4000,
        })
        setFormData((prev) => ({
          ...prev,
          projectName: option,
          licenseNumber: "",
        }))
      }
    } else if (field === "licenseNumber") {
      setFormData((prev) => ({
        ...prev,
        licenseNumber: option,
      }))
    }
    setOpenDropdown(null)
  }

  const handleSearchTermChange = (field, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleMonthRangeSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      monthRange: prev.monthRange === value ? "" : value,
    }))
  }

  const CustomDropdown = ({ label, field, options }) => {
    const searchTerm = searchTerms[field] || ""
    const filteredOptions = searchTerm
      ? options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : options

    const selectedOption = formData[field]

    return (
      <div className="relative w-full h-[90px]">
        <label className="block mb-2 text-sm font-medium text-white">{label}</label>

        <div
          className="flex items-center justify-between h-[42px] p-2 rounded-xl bg-white/10 border border-white/20 cursor-pointer w-full"
          onClick={() => toggleDropdown(field)}
        >
          <span className={`text-sm truncate ${selectedOption ? "text-white" : "text-white/60"}`}>
            {selectedOption || `Select ${label}`}
          </span>
          <ChevronDown
            size={16}
            className={`text-white/60 transition-transform flex-shrink-0 ${openDropdown === field ? "rotate-180" : ""}`}
          />
        </div>

        {openDropdown === field && (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setOpenDropdown(null)} />

            <div
              ref={dropdownScrollRef}
              className="absolute mt-1 w-full max-h-60 overflow-auto rounded-xl backdrop-blur-md bg-gray-800 border border-white/20 shadow-lg z-[9999]"
            >
              <div className="sticky top-0 bg-gray-900 p-2 border-b border-white/20">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchTermChange(field, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  autoComplete="off"
                />
              </div>

              {filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`cursor-pointer p-2 hover:bg-[#d97925] transition-colors ${
                    selectedOption === option ? "bg-white/15" : ""
                  }`}
                  onClick={() => handleOptionSelect(field, option)}
                >
                  <span className="text-white text-sm">{option}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  const MonthRangeDropdown = () => (
    <div className="relative w-full h-[90px]">
      <label className="block mb-2 text-sm font-medium text-white">Month Range</label>

      <div
        className="flex items-center justify-between h-[42px] p-2 rounded-xl bg-white/10 border border-white/20 cursor-pointer w-full"
        onClick={() => toggleDropdown("monthRange")}
      >
        <span className={`text-sm ${formData.monthRange ? "text-white" : "text-white/60"}`}>
          {formData.monthRange || "Select Month Range"}
        </span>
        <ChevronDown
          size={16}
          className={`text-white/60 transition-transform ${openDropdown === "monthRange" ? "rotate-180" : ""}`}
        />
      </div>

      {openDropdown === "monthRange" && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpenDropdown(null)} />
          <div className="absolute mt-1 w-full rounded-xl backdrop-blur-md bg-gray-800 border border-white/20 shadow-lg z-[9999]">
            {monthRangeOptions.map((option, idx) => (
              <div
                key={idx}
                className={`cursor-pointer p-3 hover:bg-[#d97925] transition-colors ${
                  formData.monthRange === option ? "bg-white/15" : ""
                }`}
                onClick={() => {
                  handleMonthRangeSelect(option)
                  setOpenDropdown(null)
                }}
              >
                <span className="text-white text-sm">{option}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )

  const handleApplyFilters = async () => {
    const { projectName, licenseNumber, monthRange, year } = formData
    const hasYear = !!year && typeof year?.format === "function" // Dayjs instance check

    if (!projectName || !licenseNumber || !monthRange || !hasYear) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Incomplete Filters",
        detail: "Check all filters to be filled",
        life: 4000,
      })
      return
    }

    setIsLoading(true)
    try {
      const client_id = sessionStorage.getItem("client_id")
      const authToken =
        sessionStorage.getItem("authToken") || sessionStorage.getItem("token") || sessionStorage.getItem("access_token")

      const apiData = {
        client_id,
        project_name: projectName,
        license_number: licenseNumber,
        month_range: monthRange,
        year: year.format("YYYY"),
      }

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      }

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }

      const response = await axios.post(`${API_BASE_URL}api/half_yearly_returns/`, apiData, {
        headers,
        responseType: "blob", // Handle file downloads
      })

      const contentType = response.headers["content-type"]
      const contentDisposition = response.headers["content-disposition"]
      const headerMsg =
        response.headers["x-message"] ||
        response.headers["x-msg"] ||
        response.headers["message"] ||
        response.headers["x-backend-message"] ||
        ""

      // If backend returned JSON or text, parse blob and show that message
      if (contentType && (contentType.includes("application/json") || contentType.startsWith("text/"))) {
        const blob = response.data
        const text = typeof blob?.text === "function" ? await blob.text() : ""
        let detail = text
        try {
          const json = JSON.parse(text)
          detail = json.message || json.detail || json.data || text
        } catch {
          // Non-JSON text; use as-is
        }

        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: detail || headerMsg || "Operation completed successfully.",
          life: 4000,
        })
      } else if (contentType && (contentType.includes("application/") || contentType.includes("image/"))) {
        // Treat as file download (e.g., PDF). After downloading, show server-provided message if any.
        let filename = "half_yearly_report.pdf"
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "")
          }
        }

        const blob = new Blob([response.data], { type: contentType })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)

        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: headerMsg || "File downloaded successfully!",
          life: 4000,
        })
      } else {
        // Unknown type; try to extract text message, fall back to header or default
        const blob = response.data
        let detail = headerMsg
        if (!detail && blob && typeof blob.text === "function") {
          try {
            detail = await blob.text()
          } catch {
            // ignore
          }
        }

        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: detail || "Filters applied successfully!",
          life: 4000,
        })
      }
    } catch (error) {
      // If the backend returned a Blob (common when responseType is 'blob'), try to parse message from it
      if (
        error?.response?.data &&
        typeof error.response.data === "object" &&
        typeof error.response.data.text === "function"
      ) {
        try {
          const text = await error.response.data.text()
          let detail = text
          try {
            const json = JSON.parse(text)
            detail = json.message || json.detail || json.error || text
          } catch {
            // non-JSON text; use as-is
          }
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: detail,
            life: 4000,
          })
          return
        } catch {
          // fall through to existing branches
        }
      }

      if (error.response?.status === 401) {
        toastRef.current?.show({
          severity: "error",
          summary: "Authentication Required",
          detail: "Please login again to access this feature.",
          life: 4000,
        })
      } else if (error.response?.status === 500) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "SOMETHING WENT WRONG!!!",
          life: 4000,
        })
      } else if (error.response?.data?.detail) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response.data.detail,
          life: 4000,
        })
      } else if (error.response?.data?.message) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response.data.message,
          life: 4000,
        })
      } else {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to apply filters. Please try again.",
          life: 4000,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 backdrop-blur-md">
          <div className="p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
            <ClimbingBoxLoader color="#d97925" size={15} />
            <p className="text-white text-lg font-semibold">Loading...</p>
          </div>
        </div>
      )}
      <div className="w-full p-6 backdrop-blur-lg bg-white/10 border border-white/20 rounded-2xl shadow-2xl">
        <Toast ref={toastRef} position="top-right" className="custom-toast" />

        <h2 className="text-xl font-semibold text-white mb-6">Filters</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <CustomDropdown label="Project Name" field="projectName" options={filterData.projectNames} />

          <CustomDropdown label="License Number" field="licenseNumber" options={filterData.licenseNumbers} />

          <MonthRangeDropdown />

          <div className="w-full h-[90px]">
            <label className="block mb-2 text-sm font-medium text-white">Year</label>
            <DatePicker
              views={["year"]}
              value={formData.year}
              onChange={(newValue) => setFormData((prev) => ({ ...prev, year: newValue }))}
              format="YYYY"
              slots={{
                openPickerIcon: () => <CalendarDays className="text-orange-500" />,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  placeholder: "Select Year",
                  InputProps: {
                    style: {
                      color: "#fff",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.2)",
                      height: "42px",
                    },
                  },
                  InputLabelProps: {
                    style: { color: "#fff" },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            className="px-6 py-2 bg-orange-500/80 hover:bg-orange-500 backdrop-blur-sm text-white rounded-lg font-medium transition-all duration-200 border border-orange-400/30"
            onClick={handleApplyFilters}
          >
            Apply Filters
          </button>
          <button
            className="px-6 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium transition-all duration-200 border border-white/20"
            onClick={() => {
              setFormData({
                projectName: "",
                licenseNumber: "",
                monthRange: "",
                year: null,
              })
              setSearchTerms({ projectName: "", licenseNumber: "" })
            }}
          >
            Clear All
          </button>
        </div>
      </div>
    </LocalizationProvider>
  )
}

export default Returns
